import { useTranslation } from "react-i18next";
import React, { useState, useEffect, useMemo } from "react";
import { Form, Input, Spin } from "antd";
import { convertToDayjs, formatToLocaleDate } from "../../../../utils/dateUtils";
import { ClassTask, ClassWithSubjects } from "../../../../types/classTaskTypes";
import TextArea from "antd/es/input/TextArea";
import LocalizedDatePicker from "components/Common/ui/LocalizedDatePicker";
import CommonSelectLite from "components/Common/CommonSelectLite";
import { getSubjectLabelFromCode } from "utils/schoolUtils";

interface TaskFormProps {
  form: any;
  initialValues?: Partial<ClassTask> & Record<string, any>;
  classData: ClassWithSubjects[];
  subjects: { value: string; label: string }[];
  isEditing: boolean;
}

function normalizeInitial(raw: any) {
  const typeNorm =
    raw?.type === "lessonPlan" ? "LessonPlan" :
      raw?.type === "homework" ? "Homework" : raw?.type;

  const subjectId =
    raw?.subjectId ??
    raw?.["classSubject.subject.id"] ??
    undefined;

  const subjectLabel =
    raw?.subject ??
    raw?.["classSubject.subject.code"] ??
    undefined;

  const classLabel = raw?.className ?? undefined;

  return {
    ...raw,
    type: typeNorm,
    subjectId,
    subjectLabel,
    classLabel,
  };
}

const TaskForm: React.FC<TaskFormProps> = ({
  form,
  initialValues,
  classData,
  subjects,
  isEditing,
}) => {
  const { t } = useTranslation();
  const norm = useMemo(() => normalizeInitial(initialValues ?? {}), [initialValues]);

  const [selectedSubjectId, setSelectedSubjectId] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  // Prepare defaults / selected subject, and IMPORTANT: reset form on mode/record change
  useEffect(() => {
    let mounted = true;
    (async () => {
      // Reset the form so stale values from previous open don't linger
      form.resetFields();

      // Seed base initial values
      const initial = {
        ...norm,
        date: norm?.date ? convertToDayjs(norm.date) : null,
        dueDate: norm?.dueDate ? convertToDayjs(norm.dueDate) : null,
      };
      form.setFieldsValue(initial);

      // Subject pre-selection logic
      if (isEditing) {
        if (norm.subjectId) setSelectedSubjectId(String(norm.subjectId));
      } else {
        if (!selectedSubjectId && subjects.length === 1) {
          const onlySubject = subjects[0].value;
          setSelectedSubjectId(onlySubject);
          form.setFieldsValue({ subjectId: onlySubject, classId: undefined });
        } else {
          setSelectedSubjectId(null); // entering create: drop any previous selection
          form.setFieldsValue({ subjectId: undefined, classId: undefined });
        }
      }

      if (mounted) setLoading(false);
    })();

    return () => {
      mounted = false;
    };
    // re-run when switching between edit/create or record id changes
  }, [isEditing, norm?.id, initialValues, subjects, form]); // norm?.id if you have an id field for the task

  const filteredClasses = useMemo(() => {
    if (!selectedSubjectId) return [];
    const filtered = classData
      .filter((cls) =>
        cls.subjects.some((subj) => subj.subjectId === selectedSubjectId)
      )
      .map((cls) => ({ value: cls.classId, label: cls.className }));
    return Array.from(new Map(filtered.map((c) => [c.value, c])).values());
  }, [selectedSubjectId, classData]);

  // Helpers to map IDs to labels (fallbacks)
  const getSubjectName = (id?: string) =>
    subjects.find((s) => s.value === id)?.label || "-";



  const getClassName = (id?: string) =>
    classData.find((c) => c.classId === id)?.className || "-";

  const getTypeName = (type?: string) => {
    if (type === "LessonPlan") return t("lesson_plan");
    if (type === "Homework") return t("homework");
    return "-";
  };

  if (loading) {
    return (
      <div style={{ padding: 12 }}>
        <Spin tip={t("loading")} />
      </div>
    );
  }

  return (
    <Form
      key={isEditing ? (norm?.id ?? "edit") : "create"} // ⬅️ force remount when switching mode/record
      form={form}
      layout="vertical"
      preserve={false} // don't preserve unmounted fields
      initialValues={{
        ...norm,
        date: norm?.date ? convertToDayjs(norm.date) : null,
        dueDate: norm?.dueDate ? convertToDayjs(norm.dueDate) : null,
      }}
    >
      {/* TYPE */}
      {isEditing ? (
        <Form.Item label={t("task_type")}>
          <Input value={getTypeName(norm?.type)} readOnly disabled />
        </Form.Item>
      ) : (
        <Form.Item
          name="type"
          label={t("task_type")}
          rules={[{ required: true, message: t("select_a_task_type") }]}
          valuePropName="value"
          getValueFromEvent={(v) => v}
        >
          <CommonSelectLite
            options={[
              { value: "LessonPlan", label: t("lesson_plan") },
              { value: "Homework", label: t("homework") },
            ]}
            optionValueKey="value"
            optionLabelKey="label"
            placeholder={t("select_a_task_type")}
          // Let Form control value/onChange – no props here
          />
        </Form.Item>
      )}

      {/* SUBJECT */}
      {isEditing ? (
        <Form.Item label={t("subject")}>
          <Input
            value={getSubjectName(norm?.subjectId ?? "") || getSubjectLabelFromCode(norm?.subjectLabel ?? "")}
            readOnly
            disabled
          />
        </Form.Item>
      ) : (
        <Form.Item
          name="subjectId"
          label={t("subject")}
          rules={[{ required: true, message: t("select_subject_for_exam") }]}
          valuePropName="value"
          getValueFromEvent={(v) => v}
        >
          <CommonSelectLite
            options={subjects}
            optionValueKey="value"
            optionLabelKey="label"
            placeholder={t("select_subject_for_exam")}
            onChange={(value: string) => {
              setSelectedSubjectId(value);
              form.setFieldsValue({ classId: undefined });
            }}
          />
        </Form.Item>
      )}

      {/* CLASS */}
      {isEditing ? (
        <Form.Item label={t("class")}>
          <Input
            value={norm?.classLabel || getClassName(norm?.classId)}
            readOnly
            disabled
          />
        </Form.Item>
      ) : (
        <Form.Item
          name="classId"
          label={t("class")}
          rules={[{ required: true, message: t("select_a_class") }]}
          valuePropName="value"
          getValueFromEvent={(v) => v}
        >
          <CommonSelectLite
            options={filteredClasses}
            optionValueKey="value"
            optionLabelKey="label"
            placeholder={t("select_a_class")}
            disabled={!selectedSubjectId}
          />
        </Form.Item>
      )}

      {/* DATE */}
      {isEditing ? (
        <Form.Item label={t("task_date")}>
          <Input
            value={norm?.date ? formatToLocaleDate(norm.date) : "-"}
            readOnly
            disabled
          />
        </Form.Item>
      ) : (
        <Form.Item
          name="date"
          label={t("task_date")}
          rules={[{ required: true, message: t("select_a_date") }]}
        >
          <LocalizedDatePicker style={{ width: "100%" }} placeholder={t("select_a_date")} fromToday />
        </Form.Item>
      )}

      {/* TOPIC */}
      <Form.Item
        name="topic"
        label={t("task_topic")}
        rules={[{ required: true, message: t("enter_a_title") }]}
      >
        <Input placeholder={t("task_topic")} />
      </Form.Item>

      {/* DESCRIPTION */}
      <Form.Item
        name="description"
        label={t("task_description")}
        rules={[{ required: true, message: t("enter_a_description") }]}
      >
        <TextArea
          placeholder={t("task_description")}
          maxLength={250}
          showCount
          autoSize={{ minRows: 3, maxRows: 6 }}
        />
      </Form.Item>

      {/* DUE DATE */}

      <Form.Item
        name="dueDate"
        label={t("due_date")}
        dependencies={["date"]}
        rules={[
          { required: true, message: t("select_a_date") },
          ({ getFieldValue }) => ({
            validator(_, value) {
              const taskDate = getFieldValue("date");
              if (!value || !taskDate) return Promise.resolve();
              if (value.isAfter(taskDate, "day")) return Promise.resolve();
              return Promise.reject(
                new Error(t("due_date_must_be_after_task_date"))
              );
            },
          }),
        ]}
      >
        <LocalizedDatePicker style={{ width: "100%" }} placeholder={t("select_a_date")} fromToday />
      </Form.Item>

    </Form>
  );
};

export default TaskForm;
